import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

export interface TsApproveSession {
  projectCode: string;
  projectName: string;
  signInAt: string;
  signOutAt: string | null;
  hours: number;
}

interface Props {
  onClose: () => void;
  detail: string;
  sessionId: string;
  sessions?: TsApproveSession[];
  workerName?: string;
  date?: string;
}

export function TsApproveModal({ onClose, detail, sessionId, sessions, workerName, date }: Props) {
  const qc = useQueryClient();
  const [approvedBy, setApprovedBy] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => api.post(`/timesheet/approve`, { sessionId, approvedBy, note: note.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet', 'report'] });
      toast.success('Approval recorded');
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div className="modal-title">⚠ Approve Multi-Site Working</div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>
        <div id="ts-approve-detail" style={{ background: 'var(--surface)', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12 }}>
          {sessions && sessions.length ? (
            <>
              {(workerName || date) ? (
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  {workerName}{workerName && date ? ' · ' : ''}{date}
                </div>
              ) : null}
              {sessions.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderTop: i > 0 ? '1px solid var(--border)' : undefined, fontSize: 11 }}>
                  <div>
                    <div style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{s.projectCode}</div>
                    <div style={{ color: 'var(--muted)', fontSize: 10 }}>{s.projectName}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{s.hours.toFixed(1)}h</div>
                    <div style={{ color: 'var(--muted)', fontSize: 10 }}>
                      {new Date(s.signInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      {' – '}
                      {s.signOutAt ? new Date(s.signOutAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            detail
          )}
        </div>
        <div style={{ marginBottom: 10 }}>
          <label className="form-label">Approved By (Manager Name)</label>
          <input className="form-input" id="ts-approve-by" placeholder="Enter manager name…" value={approvedBy} onChange={(e) => setApprovedBy(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label className="form-label">Notes (optional)</label>
          <textarea className="form-textarea" id="ts-approve-note" placeholder="Reason for approval…" value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 60, width: '100%' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => save.mutate()} disabled={save.isPending || !approvedBy.trim()}>
            {save.isPending ? 'Recording…' : '✓ Record Approval'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
